import { useCallback, useRef } from 'react'
import { UseToastOptions } from '@chakra-ui/react'
import { updateDoc } from 'firebase/firestore'
import FirestoreUser, {
  Diagnosis,
  DiagnosisChange,
  Profile,
} from '../../../../../types/FirestoreUser'
import userDoc from '../../../../../utils/userDoc'
import { isDateEmpty } from '../../../../../utils/dateValidation'

interface UseProfileSubmitParams {
  uid: string
  firestoreUser: FirestoreUser
  toast: (options: UseToastOptions) => void
  prevDiagnosisRef: React.MutableRefObject<string | undefined>
  setProfile: React.Dispatch<React.SetStateAction<Profile>>
  setIsSaved: (saved: boolean) => void
  setLoading: (loading: boolean) => void
  handleError: (error: unknown) => void
  uploadDiagnosisDocuments: (profile: Profile) => Promise<Profile>
  files: FileList | null
}

interface SubmitResult {
  success: boolean
  isFirstCompletion: boolean
}

/**
 * Custom hook for handling profile submission to Firestore
 * Extracts the update logic from PersonalTab for better separation of concerns
 */
export default function useProfileSubmit({
  uid,
  firestoreUser,
  toast,
  prevDiagnosisRef,
  setProfile,
  setIsSaved,
  setLoading,
  handleError,
  uploadDiagnosisDocuments,
  files,
}: UseProfileSubmitParams) {
  // Track if profile was incomplete before this save
  const wasProfileIncompleteRef = useRef(
    !firestoreUser.profile?.agreedToProfileCheckbox,
  )

  const submitProfile = useCallback(
    async (profile: Profile): Promise<SubmitResult> => {
      if (!uid) {
        alert('Not logged in!')
        return { success: false, isFirstCompletion: false }
      }

      setLoading(true)

      try {
        // Handle file uploads
        let updatedProfile = profile
        if (files && files.length > 0) {
          updatedProfile = await uploadDiagnosisDocuments(profile)
        }

        // Ensure diagnosisVerified is set
        if (!updatedProfile.diagnosisVerified) {
          updatedProfile.diagnosisVerified = false
        }

        // Clean up profile data
        updatedProfile.firstName = updatedProfile.firstName?.trim()
        updatedProfile.lastName = updatedProfile.lastName?.trim()
        updatedProfile.searchFirstName = updatedProfile.firstName?.toLowerCase()
        updatedProfile.searchLastName = updatedProfile.lastName?.toLowerCase()

        // Remove incomplete/empty dates before saving
        if (isDateEmpty(updatedProfile.diagnosisDate)) {
          const { diagnosisDate, ...profileWithoutDiagnosisDate } =
            updatedProfile
          updatedProfile = profileWithoutDiagnosisDate as typeof updatedProfile
        }
        if (isDateEmpty(updatedProfile.firstSymptomsDate)) {
          const { firstSymptomsDate, ...profileWithoutFirstSymptomsDate } =
            updatedProfile
          updatedProfile =
            profileWithoutFirstSymptomsDate as typeof updatedProfile
        }

        // Build diagnosis change history
        const diagnosisChangeHistory = buildDiagnosisChangeHistory(
          prevDiagnosisRef.current,
          updatedProfile.diagnosis,
          firestoreUser,
        )

        // Update the document
        await updateDoc(userDoc(uid), {
          profile: updatedProfile,
          diagnosisChangeHistory,
        })

        setProfile(updatedProfile)
        setIsSaved(true)
        setLoading(false)

        toast({
          title: 'Profile updated',
          status: 'success',
          duration: 2000,
          isClosable: true,
        })

        // Check if this is first profile completion
        const isFirstCompletion =
          wasProfileIncompleteRef.current &&
          !!updatedProfile.agreedToProfileCheckbox

        return { success: true, isFirstCompletion }
      } catch (error) {
        console.error('Error updating profile:', error)
        handleError(error)
        setLoading(false)
        return { success: false, isFirstCompletion: false }
      }
    },
    [
      uid,
      firestoreUser,
      toast,
      prevDiagnosisRef,
      setProfile,
      setIsSaved,
      setLoading,
      handleError,
      uploadDiagnosisDocuments,
      files,
    ],
  )

  return { submitProfile }
}

/**
 * Build the diagnosis change history array based on diagnosis changes
 */
function buildDiagnosisChangeHistory(
  prevDiagnosis: string | undefined,
  newDiagnosis: string | undefined,
  firestoreUser: FirestoreUser,
): DiagnosisChange[] {
  let diagnosisChangeHistory: DiagnosisChange[] =
    firestoreUser.diagnosisChangeHistory || []

  // Track ALL diagnosis changes
  if (prevDiagnosis && newDiagnosis && prevDiagnosis !== newDiagnosis) {
    diagnosisChangeHistory = [
      ...diagnosisChangeHistory,
      {
        from: prevDiagnosis as Diagnosis,
        to: newDiagnosis as Diagnosis,
        date: new Date().toISOString(),
      },
    ]
  }

  return diagnosisChangeHistory
}
